import React, { useEffect, useState } from 'react';
import {
    Card, CardBody, Col, Container, Row, Badge, Table, Button, Spinner,
    Modal, ModalHeader, ModalBody
} from 'reactstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api';
import { inkFor } from '../../utils/themeColor';
// Owner policy (2026-08-17): ONE company shift (8:00–4:30) — the Round-17
// "Employees without a shift" backfill panel is retired (a NULL shift now
// simply means the one standard shift). MissingShiftPanel.js and its
// registration endpoints stay on disk, unreferenced, for rollback.
// import MissingShiftPanel from './MissingShiftPanel'; // Round 17 (retired)
import {
    DashboardPageHeader,
    CompactStatsRow,
    EmptyState,
    TableSkeleton,
} from '../../Components/Common/DashboardKit';

// ─────────────────────────────────────────────────────────────────────────────
// Phase 3 / Feature 2 — HR Landing Dashboard (Batch P4 superset).
//
// One page, one fetch, five sections:
//   1. Quick action launchpad — links to every HR admin page
//   2. Headcount  (totals + IDL/DL split)
//   3. Quick stats (pending FTO, COA, reviews YTD, new hires)
//   4. Headcount by dept
//   5. This month: new hires
//   6. Upcoming: birthdays + work anniversaries (next 30 days)
//
// Calls GET /api/dashboard/hr.  HR/IT only — route-gated in allRoutes.js.
//
// ── Round 1 UI modernization (2026) ──────────────────────────────────────────
// Presentation-only pass. NOTHING about the data contract changed:
//   • Same single fetch: api.get('dashboard/hr').
//   • Same response field reads: data.headcount.{totalActive,salaried,hourly,
//     byDept[]}, data.quickActions.{inactiveEmployees,pendingFtoRequests,
//     pendingCoaRequests,completedReviewsThisYear}, data.thisMonth.{newHires,
//     newHireList[]}, data.upcoming.{birthdays[],workAnniversaries[]},
//     data.generatedAtUtc.
//   • Same 9 quick-action routes, same labels, same blurbs, same order.
// What changed (visual only):
//   • Emoji icons (👥 🔄 📋 …) → Remix icon classes (ri-*) for a consistent,
//     accessible, professional icon set. Each card now carries an aria-label.
//   • Added DashboardPageHeader (matches every other module landing).
//   • Headcount cards → DashboardKit StatTile via CompactStatsRow (animated,
//     glanceable). Same four numbers.
//   • `if (!data) return null` (blank screen) → real loading skeletons +
//     an EmptyState error block with a retry button. The page never renders
//     to nothing anymore.
//   • Section headers (🎂 / 🎉) de-emojified into Remix icons.
// ─────────────────────────────────────────────────────────────────────────────

// Quick Action card config — single source of truth.
// `icon` is now a Remix icon class (was an emoji glyph). Routes/labels/blurbs
// are unchanged.
const QUICK_ACTIONS = [
    {
        // HR Round 1 / Issue 2 — Unified HR Review Dashboard. One queue for
        // every pending FTO / COA / Profile / missing-punch item. First tile
        // so it becomes the natural starting point for HR review work.
        icon: 'ri-inbox-unarchive-line', label: 'HR Review',
        blurb: 'All pending FTO, COA, profile, and punch items in one queue.',
        link: '/hr/review',
        group: 'Approvals & review',
        color: 'primary',
    },
    {
        icon: 'ri-check-double-line', label: 'FTO / COA Approvals',
        blurb: 'Review and approve employee FTO and COA requests.',
        link: '/attendance/approvals',
        group: 'Approvals & review',
        color: 'warning',
    },
    {
        // HR Round 2 / Issue 6 — Late-In Review + email workflow.
        icon: 'ri-alarm-warning-line', label: 'Late-In Review',
        blurb: 'Who clocked in late today — notify employee and supervisor.',
        link: '/hr/late-in',
        group: 'Approvals & review',
        color: 'danger',
    },
    {
        // HR Round 3 / Issue 5 — database-generated attendance report.
        icon: 'ri-calendar-todo-line', label: 'Attendance Report',
        blurb: 'Master-sheet, weekly and monthly attendance views from the database.',
        link: '/hr/attendance-report',
        group: 'Reports & records',
        color: 'info',
    },
    {
        // HR Round 3 / Issue 5 — late-in rollups by employee/dept/supervisor.
        icon: 'ri-timer-flash-line', label: 'Late-In Report',
        blurb: 'Yearly and weekly late-in summaries, exportable to CSV.',
        link: '/hr/late-in-report',
        group: 'Reports & records',
        color: 'secondary',
    },
    // ── Tile hidden per HR request (2026-06). Frontend-only: route
    //    /hr/overtime-review is KEPT in allRoutes.js; only the tile is removed.
    //    (Supersedes changes-hide-hr-tiles for this file.) Re-enable by uncommenting.
    // {
    //     icon: 'ri-time-line', label: 'Overtime Review',
    //     blurb: 'Review and approve overtime requests, grouped by week.',
    //     link: '/hr/overtime-review',
    //     color: 'info',
    // },
    {
        icon: 'ri-user-follow-line', label: 'Profile Approvals',
        blurb: 'Review employee self-service profile change requests.',
        link: '/hr/profile-approvals',
        group: 'Approvals & review',
        color: 'primary',
    },
    {
        icon: 'ri-refresh-line', label: 'Reactivate Employee',
        blurb: 'Bring a terminated employee back to active status.',
        link: '/hr/reactivate',
        group: 'People',
        color: 'info',
    },
    {
        icon: 'ri-calendar-event-line', label: 'Year-End FTO',
        blurb: 'Preview & commit annual FTO carry-over for all employees.',
        link: '/hr/fto-year-end',
        group: 'FTO admin',
        color: 'warning',
    },
    {
        icon: 'ri-money-dollar-circle-line', label: 'Paid FTO',
        blurb: 'Log paid FTO consumption + company-wide history.',
        link: '/hr/paid-fto',
        group: 'FTO admin',
        color: 'success',
    },
    // Phase 2 (P6) additions:
    {
        icon: 'ri-bar-chart-box-line', label: 'FTO HR Summary',
        blurb: 'One-page snapshot of every employee\'s FTO balance.',
        link: '/hr/fto-summary',
        group: 'FTO admin',
        color: 'primary',
    },
    // ── Tile hidden per HR request (2026-06). Frontend-only: route /hr/comp-days
    //    is KEPT in allRoutes.js; only the tile is removed.
    //    (Supersedes changes-hide-hr-tiles for this file.) Re-enable by uncommenting.
    // {
    //     icon: 'ri-gift-line', label: 'Compensation Days',
    //     blurb: 'Grant comp days that add to an employee\'s FTO balance.',
    //     link: '/hr/comp-days',
    //     color: 'success',
    // },
    {
        icon: 'ri-sun-line', label: 'FTO Holidays',
        blurb: 'Define paid holidays + factory shutdown days.',
        link: '/hr/holidays',
        group: 'FTO admin',
        color: 'info',
    },
    {
        icon: 'ri-calendar-todo-line', label: 'Year-End Request',
        blurb: 'Per-employee 53-week FTO consumption audit view.',
        link: '/hr/year-end-request',
        group: 'FTO admin',
        color: 'warning',
    },
];

const TONE_HEX = {
    primary: '#405189', info: '#299cdb', secondary: '#74788d',
    warning: '#f7b84b', success: '#0ab39c', danger: '#f06548',
};

const HrDashboard = () => {
    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr]         = useState(null);

    // HR-UI round: department code → name map, for "Headcount by dept" labels
    // and the drill-down modal. Sourced from /api/department (same endpoint the
    // HR Reports page uses); kept on the FE so the dashboard service stays free
    // of the external Epicor dependency.
    const [deptNames, setDeptNames] = useState({});   // { [jcDept1]: description }

    // HR-UI round: drill-down modal state for clickable headcount / data-quality
    // tiles. The list is lazy-loaded from /api/dashboard/hr/employees?group=...
    const [drill, setDrill] = useState({ open: false, title: '', group: '', loading: false, rows: [], error: null });

    const load = async () => {
        setLoading(true);
        setErr(null);
        try {
            const r = await api.get('dashboard/hr');
            setData(r.data);
        } catch (e) {
            const msg = e?.response?.data?.message || 'Failed to load dashboard.';
            setErr(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    // Load department names once (best-effort; dept rows fall back to the code).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                // Owner clarification (2026-08-17): read the working ERP-SQL
                // endpoint instead of the Epicor REST proxy (same data).
                const res = await api.get('registration/departments');
                const list = Array.isArray(res.data) ? res.data : [];
                if (!cancelled) {
                    const map = {};
                    list.forEach(d => { if (d?.id != null) map[String(d.id)] = d.description || ''; });
                    setDeptNames(map);
                }
            } catch { /* names are a nicety; codes still render */ }
        })();
        return () => { cancelled = true; };
    }, []);

    const deptLabel = (code) => {
        const name = deptNames[String(code)];
        return name ? `${name} — ${code}` : String(code);
    };

    // Open the drill-down modal and lazy-load the employees behind a count.
    const openDrill = async (group, title) => {
        setDrill({ open: true, title, group, loading: true, rows: [], error: null });
        try {
            const r = await api.get('dashboard/hr/employees', { params: { group } });
            setDrill(d => ({ ...d, loading: false, rows: Array.isArray(r.data) ? r.data : [] }));
        } catch (e) {
            const msg = e?.response?.data?.message || 'Failed to load employees.';
            setDrill(d => ({ ...d, loading: false, error: msg }));
        }
    };
    const closeDrill = () => setDrill(d => ({ ...d, open: false }));

    const fmtDate = (s) => s ? new Date(s).toLocaleDateString() : '—';

    // Quick-action launchpad — rendered in every state so the page is never blank.
    const quickActionsGrid = (
        <Card className="shadow-sm border-0 mb-3">
            <CardBody>
                <h6 className="text-muted fw-semibold mb-3">
                    <i className="ri-flashlight-line align-bottom me-1" />
                    Quick actions
                </h6>
                {/* HR polish round — the 13 tiles were one undifferentiated wall;
                    they now render under the SAME category labels as the Human
                    Resources landing page, so the two screens teach one mental
                    model. Tiles, routes, and behavior unchanged. */}
                {['Approvals & review', 'FTO admin', 'Reports & records', 'People'].map(groupName => (
                <div key={groupName} className="mb-2">
                <div className="text-uppercase text-muted fw-semibold mb-2" style={{ fontSize: 11, letterSpacing: 0.5 }}>
                    {groupName}
                </div>
                <Row className="g-3 mb-2">
                    {QUICK_ACTIONS.filter(qa => (qa.group || 'People') === groupName).map(qa => {
                        const hex = TONE_HEX[qa.color] || TONE_HEX.primary;
                        return (
                            <Col key={qa.link} sm={6} lg={4} xxl={3}>
                                <Link
                                    to={qa.link}
                                    className="text-decoration-none"
                                    aria-label={qa.label}
                                >
                                    <Card
                                        className="h-100 border-0 shadow-sm hr-qa-card"
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <CardBody className="d-flex align-items-start gap-3">
                                            <div
                                                className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                                                style={{
                                                    width: 44, height: 44, borderRadius: 10,
                                                    background: `${hex}1A`, color: inkFor(hex),
                                                }}
                                            >
                                                <i className={qa.icon} style={{ fontSize: 22 }} />
                                            </div>
                                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                <h6 className="mb-1 fw-semibold text-body">{qa.label}</h6>
                                                <p className="text-muted small mb-0">{qa.blurb}</p>
                                            </div>
                                            <i className="ri-arrow-right-line text-muted flex-shrink-0" />
                                        </CardBody>
                                    </Card>
                                </Link>
                            </Col>
                        );
                    })}
                </Row>
                </div>
                ))}
            </CardBody>
            <style>{`
                .hr-qa-card { transition: transform .15s ease, box-shadow .15s ease; }
                .hr-qa-card:hover { transform: translateY(-2px); box-shadow: 0 .5rem 1rem rgba(0,0,0,.12) !important; }
            `}</style>
        </Card>
    );

    const header = (
        <DashboardPageHeader
            icon="ri-dashboard-3-line"
            eyebrow="People"
            title="HR Dashboard"
            subtitle={
                data
                    ? `Snapshot generated ${new Date(data.generatedAtUtc).toLocaleString()}`
                    : 'Counters, pending items, anniversaries'
            }
            tone="violet"
            breadcrumb={[
                { label: 'Dashboards', to: '/' },
                { label: 'Human Resource', to: '/humanresource' },
                { label: 'HR Dashboard' },
            ]}
            actions={
                <Button color="light" onClick={load} disabled={loading} title="Refresh">
                    <i className={`ri-refresh-line align-bottom me-1 ${loading ? 'hr-spin' : ''}`} />
                    {loading ? 'Refreshing…' : 'Refresh'}
                </Button>
            }
        />
    );

    // ── Error state — never a blank screen ───────────────────────────────────
    if (err && !data) {
        return (
            <div className="page-content">
                <Container fluid>
                    {header}
                    <Card className="shadow-sm border-0">
                        <CardBody>
                            <EmptyState
                                icon="ri-error-warning-line"
                                tone="danger"
                                title="Couldn't load the HR dashboard"
                                message={err}
                                action={
                                    <Button color="primary" onClick={load}>
                                        <i className="ri-refresh-line align-bottom me-1" /> Try again
                                    </Button>
                                }
                            />
                        </CardBody>
                    </Card>
                </Container>
            </div>
        );
    }

    // ── Loading state — skeletons instead of a bare spinner / blank ──────────
    if (loading && !data) {
        return (
            <div className="page-content">
                <Container fluid>
                    {header}
                    {/* Simplification round (owner request, 2026-07): the quick-action
                    launchpad duplicated the Human Resources landing categories —
                    HR saw the same tiles twice. The dashboard is now purely what
                    its name says: counters, pending items, anniversaries, and
                    data quality. All navigation lives on the HR landing page.
                    (quickActionsGrid is kept in code for easy restore.) */}
                    <CompactStatsRow
                        loading
                        stats={[
                            { key: 'a', label: 'Total active',  icon: 'ri-team-line',        tone: 'primary' },
                            { key: 'b', label: 'Exempt (IDL)',     icon: 'ri-user-star-line',    tone: 'info' },
                            { key: 'c', label: 'Non-Exempt (DL)',  icon: 'ri-user-settings-line', tone: 'success' },
                            { key: 'd', label: 'Inactive',       icon: 'ri-user-unfollow-line', tone: 'secondary' },
                        ]}
                        className="mb-3"
                    />
                    <Card className="shadow-sm border-0">
                        <CardBody>
                            <Table className="mb-0"><tbody>
                                <TableSkeleton rows={5} columns={3} />
                            </tbody></Table>
                        </CardBody>
                    </Card>
                </Container>
            </div>
        );
    }

    if (!data) {
        // Defensive: no error, no data (e.g. empty body). Show empty, not blank.
        return (
            <div className="page-content">
                <Container fluid>
                    {header}
                    <Card className="shadow-sm border-0">
                        <CardBody>
                            <EmptyState
                                icon="ri-inbox-line"
                                title="No dashboard data"
                                message="The HR dashboard returned no data."
                                action={
                                    <Button color="primary" onClick={load}>
                                        <i className="ri-refresh-line align-bottom me-1" /> Reload
                                    </Button>
                                }
                            />
                        </CardBody>
                    </Card>
                </Container>
            </div>
        );
    }

    // ── Loaded — headcount tiles (same four numbers, animated) ───────────────
    // HR-UI round: each tile is now clickable and opens a modal listing the
    // employees behind the number (lazy-loaded from the drill-down endpoint).
    const headcountStats = [
        { key: 'totalActive', label: 'Total active',   value: data.headcount.totalActive,        icon: 'ri-team-line',          tone: 'primary',   onClick: () => openDrill('totalActive', 'Total active employees') },
        { key: 'salaried',    label: 'Exempt (IDL)',     value: data.headcount.salaried,           icon: 'ri-user-star-line',     tone: 'info',      onClick: () => openDrill('salaried', 'Exempt (IDL) employees') },
        { key: 'hourly',      label: 'Non-Exempt (DL)',  value: data.headcount.hourly,             icon: 'ri-user-settings-line', tone: 'success',   onClick: () => openDrill('hourly', 'Non-Exempt (DL) employees') },
        { key: 'inactive',    label: 'Inactive',       value: data.quickActions.inactiveEmployees, icon: 'ri-user-unfollow-line', tone: 'secondary', onClick: () => openDrill('inactive', 'Inactive employees') },
    ];

    return (
        <div className="page-content">
            <Container fluid>
                {header}

                {/* ─── Quick Actions launchpad ────────────────────────────── */}
                {/* Simplification round (owner request, 2026-07): the quick-action
                    launchpad duplicated the Human Resources landing categories —
                    HR saw the same tiles twice. The dashboard is now purely what
                    its name says: counters, pending items, anniversaries, and
                    data quality. All navigation lives on the HR landing page.
                    (quickActionsGrid is kept in code for easy restore.) */}

                {/* ─── Headcount (tiles are clickable → drill-down modal) ──── */}
                <CompactStatsRow stats={headcountStats} className="mb-3" />
                {/* HR-UI round: the standalone "Reactivate inactive employees" link
                    was removed here — it duplicated the "Reactivate Employee" Quick
                    Action tile above, and the Inactive tile is now clickable. */}

                {/* Round 17 — shift backfill mini-tool (renders only while
                    active employees with NULL Shift exist). */}

                {/* ─── Data quality (Batch 2 / R6, gap #5) ─────────────────── */}
                {data.dataQuality && (
                    <Card className="shadow-sm border-0 mb-3">
                        <CardBody>
                            <h6 className="text-muted fw-semibold mb-3">
                                <i className="ri-error-warning-line align-bottom me-1" /> Data quality
                                <span className="text-muted fw-normal ms-2" style={{ fontSize: 12 }}>
                                    (active employees needing HR attention)
                                </span>
                            </h6>
                            <Row className="g-3">
                                {[
                                    { label: 'Missing photos',     value: data.dataQuality.missingPhotos,    icon: 'ri-image-line',        tone: 'warning', group: 'missingPhotos' },
                                    { label: 'Missing titles',      value: data.dataQuality.missingTitles,    icon: 'ri-price-tag-3-line',  tone: 'warning', group: 'missingTitles' },
                                    { label: 'Invalid hire dates',  value: data.dataQuality.invalidHireDates, icon: 'ri-calendar-close-line', tone: 'danger', group: 'invalidHireDates' },
                                    { label: 'Missing extension',   value: data.dataQuality.missingExtension, icon: 'ri-phone-line',        tone: 'warning', group: 'missingExtension' },
                                    // Round HR-PA (Part 15): employees without an
                                    // email silently miss every FTO/COA notification.
                                    { label: 'Missing email',       value: data.dataQuality.missingEmail,     icon: 'ri-mail-line',         tone: 'warning', group: 'missingEmail' },
                                ].map(dq => (
                                    <Col key={dq.label} xs={6} lg={3}>
                                        <Card
                                            className="border-0 h-100"
                                            style={{ background: 'var(--vz-light, #f3f6f9)', cursor: 'pointer' }}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => openDrill(dq.group, dq.label)}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDrill(dq.group, dq.label); } }}
                                            title={`View employees: ${dq.label}`}
                                        >
                                            <CardBody className="d-flex align-items-center gap-3 py-3">
                                                <i className={`${dq.icon} fs-3 text-${dq.tone}`} />
                                                <div>
                                                    <div className="fs-4 fw-bold">{dq.value}</div>
                                                    <div className="text-muted small">{dq.label}</div>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                            <div className="text-end mt-2">
                                <Link to="/humanresource?tab=1" className="small">
                                    Manage employees <i className="ri-arrow-right-line align-bottom" />
                                </Link>
                            </div>
                        </CardBody>
                    </Card>
                )}

                {/* ─── Stats + dept ─────────────────────────────────────────── */}
                <Row className="mb-3">
                    <Col md={6}>
                        <Card className="shadow-sm border-0 h-100"><CardBody>
                            <h6 className="text-muted fw-semibold mb-3">
                                <i className="ri-pie-chart-2-line align-bottom me-1" /> Quick stats
                            </h6>
                            <Row>
                                <Col xs={6}>
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <span>Pending FTO</span>
                                        <Badge color={data.quickActions.pendingFtoRequests > 0 ? 'warning' : 'light'} pill>
                                            {data.quickActions.pendingFtoRequests}
                                        </Badge>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <span>Pending COA</span>
                                        <Badge color={data.quickActions.pendingCoaRequests > 0 ? 'warning' : 'light'} pill>
                                            {data.quickActions.pendingCoaRequests}
                                        </Badge>
                                    </div>
                                </Col>
                                <Col xs={6}>
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <span>Reviews YTD</span>
                                        <Badge color="info" pill>{data.quickActions.completedReviewsThisYear}</Badge>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <span>New hires this month</span>
                                        <Badge color="success" pill>{data.thisMonth.newHires}</Badge>
                                    </div>
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <span>In first 90 days</span>
                                        <Badge color={(data.probation?.count || 0) > 0 ? 'primary' : 'light'} pill>{data.probation?.count || 0}</Badge>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody></Card>
                    </Col>

                    <Col md={6}>
                        <Card className="shadow-sm border-0 h-100"><CardBody>
                            <h6 className="text-muted fw-semibold mb-3">
                                <i className="ri-building-line align-bottom me-1" /> Headcount by dept
                            </h6>
                            {data.headcount.byDept.length === 0 ? (
                                <EmptyState compact icon="ri-building-line"
                                    title="No department data" />
                            ) : (
                                <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                                    <Table size="sm" className="mb-0 align-middle">
                                        <tbody>
                                            {data.headcount.byDept.map(d => (
                                                <tr
                                                    key={d.dept}
                                                    style={{ cursor: 'pointer' }}
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => openDrill(`dept:${d.dept}`, `Department — ${deptLabel(d.dept)}`)}
                                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDrill(`dept:${d.dept}`, `Department — ${deptLabel(d.dept)}`); } }}
                                                    title="View employees in this department"
                                                >
                                                    <td>{deptLabel(d.dept)}</td>
                                                    <td className="text-end" style={{ width: 60 }}>
                                                        <Badge color="light" className="text-muted border">{d.count}</Badge>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </CardBody></Card>
                    </Col>
                </Row>

                {/* ─── This month + upcoming ───────────────────────────────── */}
                <Row className="mb-3">
                    <Col md={6}>
                        <Card className="shadow-sm border-0 mb-3"><CardBody>
                            <h6 className="text-muted fw-semibold mb-3">
                                <i className="ri-user-add-line align-bottom me-1" /> New hires this month
                            </h6>
                            {data.thisMonth.newHireList.length === 0 ? (
                                <EmptyState compact icon="ri-user-add-line"
                                    title="No new hires this month" />
                            ) : (
                                <Table size="sm" className="mb-0 align-middle">
                                    <thead><tr><th scope="col">Name</th><th scope="col">Title</th><th scope="col">Hire date</th></tr></thead>
                                    <tbody>
                                        {data.thisMonth.newHireList.map(h => (
                                            <tr key={h.empId}>
                                                <td>{h.fullName}</td>
                                                <td>{h.title || '—'}</td>
                                                <td>{fmtDate(h.hireDate)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </CardBody></Card>

                        {/* Owner 2026-10-20: "90 days employee tab for review purpose, probation period,
                            from hire date to 90 days". Everyone active within 90 days of the hire date;
                            a slipped review stays listed 14 more days as past. */}
                        <Card className="shadow-sm border-0"><CardBody>
                            <h6 className="text-muted fw-semibold mb-1">
                                <i className="ri-hourglass-line align-bottom me-1" /> First 90 days — probation review
                            </h6>
                            <p className="text-muted small mb-3">Everyone within 90 days of their hire date. Review by the 90th day.</p>
                            {!data.probation || data.probation.rows.length === 0 ? (
                                <EmptyState compact icon="ri-hourglass-line"
                                    title="Nobody in their first 90 days" />
                            ) : (
                                <Table size="sm" className="mb-0 align-middle">
                                    <thead><tr><th scope="col">Name</th><th scope="col">Title</th><th scope="col">Hire date</th><th scope="col">Day</th><th scope="col">Review by</th><th scope="col" /></tr></thead>
                                    <tbody>
                                        {data.probation.rows.map(p => (
                                            <tr key={p.empId} className={p.status === 'past' ? 'table-danger' : p.status === 'due' ? 'table-warning' : undefined}>
                                                <td>{p.fullName}</td>
                                                <td>{p.title || '—'}</td>
                                                <td>{fmtDate(p.hireDate)}</td>
                                                <td>{p.dayNumber < 1 ? <span className="text-muted">starts in {1 - p.dayNumber}d</span> : `${Math.min(p.dayNumber, data.probation.days || 90)} of ${data.probation.days || 90}`}</td>
                                                <td>{fmtDate(p.reviewDate)}</td>
                                                <td className="text-end text-nowrap">
                                                    {p.status === 'past' ? <Badge color="danger">past 90 days</Badge>
                                                        : p.status === 'due' ? <Badge color="warning">{p.daysLeft === 0 ? 'review today' : `review in ${p.daysLeft}d`}</Badge>
                                                        : p.status === 'starts' ? <Badge color="light" className="text-muted border">not started</Badge>
                                                        : <span className="text-muted small">{p.daysLeft}d left</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </CardBody></Card>
                    </Col>

                    <Col md={6}>
                        <Card className="shadow-sm border-0 h-100"><CardBody>
                            <h6 className="text-muted fw-semibold mb-3">
                                <i className="ri-cake-2-line align-bottom me-1" /> Birthdays — next 30 days
                            </h6>
                            {data.upcoming.birthdays.length === 0 ? (
                                <p className="text-muted small mb-3">No upcoming birthdays.</p>
                            ) : (
                                <Table size="sm" className="mb-3 align-middle">
                                    <tbody>
                                        {data.upcoming.birthdays.map(b => (
                                            <tr key={b.empId}>
                                                <td>{b.fullName}</td>
                                                <td className="text-end">{fmtDate(b.date)}</td>
                                                <td className="text-end text-muted">{b.daysAway === 0 ? 'today' : `in ${b.daysAway}d`}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}

                            <h6 className="text-muted fw-semibold mb-3">
                                <i className="ri-medal-line align-bottom me-1" /> Work anniversaries — next 30 days
                            </h6>
                            {data.upcoming.workAnniversaries.length === 0 ? (
                                <p className="text-muted small mb-0">No upcoming anniversaries.</p>
                            ) : (
                                <Table size="sm" className="mb-0 align-middle">
                                    <tbody>
                                        {data.upcoming.workAnniversaries.map(a => (
                                            <tr key={a.empId}>
                                                <td>{a.fullName}</td>
                                                <td className="text-end">
                                                    {a.years} yr{a.years === 1 ? '' : 's'}
                                                    {a.isOneYearMark && <Badge color="warning" className="ms-2">Review</Badge>}
                                                </td>
                                                <td className="text-end text-muted">{a.daysAway === 0 ? 'today' : `in ${a.daysAway}d`}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </CardBody></Card>
                    </Col>
                </Row>

                {/* ─── HR-UI round: drill-down modal for clickable tiles/cards ── */}
                <Modal isOpen={drill.open} toggle={closeDrill} size="lg" scrollable>
                    <ModalHeader toggle={closeDrill}>
                        {drill.title}
                        {!drill.loading && !drill.error && (
                            <span className="text-muted ms-2" style={{ fontSize: 13 }}>
                                ({drill.rows.length})
                            </span>
                        )}
                    </ModalHeader>
                    <ModalBody>
                        {drill.loading ? (
                            <div className="text-center py-4"><Spinner size="sm" /> <span className="ms-2 text-muted">Loading…</span></div>
                        ) : drill.error ? (
                            <EmptyState compact icon="ri-error-warning-line" title={drill.error} />
                        ) : drill.rows.length === 0 ? (
                            <EmptyState compact icon="ri-user-search-line" title="No employees in this group." />
                        ) : (
                            <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                <Table size="sm" className="mb-0 align-middle">
                                    <thead>
                                        <tr className="text-muted">
                                            <th>Emp ID</th>
                                            <th>Name</th>
                                            <th>Title</th>
                                            <th>Dept</th>
                                            <th>Type</th>
                                            <th>Hire date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {drill.rows.map(r => (
                                            <tr key={r.empId}>
                                                <td>{r.empId}</td>
                                                <td>
                                                    {r.fullName}
                                                    {r.isTemp && <Badge color="info" className="ms-2" pill>Temp/Intern</Badge>}
                                                </td>
                                                <td>{r.title || '—'}</td>
                                                <td>{r.dept ? deptLabel(r.dept) : '—'}</td>
                                                <td>{r.expenseCode || '—'}</td>
                                                <td>{fmtDate(r.hireDate)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </ModalBody>
                </Modal>
            </Container>
            <style>{`
                .hr-spin { animation: hr-spin-kf .9s linear infinite; display:inline-block; }
                @keyframes hr-spin-kf { from{transform:rotate(0)} to{transform:rotate(360deg)} }
            `}</style>
        </div>
    );
};

export default HrDashboard;
